import { Link } from 'react-router-dom';
import { Building2, Users, Shield, Target, BarChart3, Download, Clock, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BlockyCharacter3D from '../components/BlockyCharacter3D';

const ForSchoolsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream via-warm-white to-mint-green/10">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white border-2 border-mint-green/30 rounded-full px-4 py-2 mb-8 shadow-md">
              <Building2 className="w-4 h-4 text-mint-green" />
              <span className="font-display text-sm text-mint-green font-bold">FOR SCHOOLS</span>
            </div>
            
            <h1 className="font-display text-5xl md:text-7xl text-text-dark mb-6" data-testid="schools-headline">
              THE TYPING TOOL YOUR<br />
              <span className="text-mint-green">SENCO WILL LOVE</span>
            </h1>
            
            <p className="font-body text-lg text-text-medium max-w-2xl mx-auto mb-8">
              Built from the ground up for children with handwriting difficulties. 
              No timed pressure. Real progress tracking. Complete curriculum alignment.
            </p>
            
            {/* Character row */}
            <div className="flex justify-center gap-3 mb-10">
              <BlockyCharacter3D size={60} skinTone="#3D2314" hairColor="#4169E1" shirtColor="#4A90D9" pantsColor="#3B82F6" hairStyle="braids" accessory="glasses" />
              <BlockyCharacter3D size={60} skinTone="#FFE0BD" hairColor="#FF69B4" shirtColor="#EC4899" pantsColor="#1F2937" hairStyle="pigtails" accessory="bow" />
              <BlockyCharacter3D size={60} skinTone="#5C3A21" hairColor="#1a1a1a" shirtColor="#22C55E" pantsColor="#78716C" hairStyle="afro" accessory="headband" />
              <BlockyCharacter3D size={60} skinTone="#D4A574" hairColor="#00CED1" shirtColor="#F59E0B" pantsColor="#7C3AED" hairStyle="bob" eyeStyle="happy" />
              <BlockyCharacter3D size={60} skinTone="#8D5524" hairColor="#EF4444" shirtColor="#A855F7" pantsColor="#3B82F6" hairStyle="ponytail" accessory="catears" />
            </div>
            
            <Link
              to="/trial"
              data-testid="schools-trial-btn"
              className="font-display uppercase tracking-wider bg-gradient-to-r from-mint-green to-sky-blue text-white px-10 py-4 text-xl rounded-2xl hover:scale-105 transition-all shadow-lg inline-block"
            >
              Get Started - It's Free
            </Link>
            <p className="font-body text-sm text-text-medium mt-4">100% free for every school and pupil &middot; No card required</p>
          </div>
        </div>
      </section>
      
      {/* SEND Box */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border-2 border-mint-green rounded-2xl p-8 shadow-lg" data-testid="send-box">
            <div className="flex items-center gap-2 mb-6">
              <Shield className="w-6 h-6 text-mint-green" />
              <h2 className="font-display text-2xl text-mint-green text-center">DESIGNED FOR ALL LEARNERS</h2>
            </div>
            
            <p className="font-body text-text-medium text-center mb-8">
              TypeTown helps every child improve their typing skills, including those with additional needs such as DCD, dyspraxia, or hypermobility.
            </p>
            
            <div className="space-y-3">
              {[
                'No timed pressure - accuracy is celebrated over speed',
                'Large, clear visual keyboard with finger-colour guidance',
                'Reduced-motion mode for sensory needs',
                'Screen reader compatible (WCAG 2.1 AA)',
                'Teacher can adjust difficulty per pupil'
              ].map((feature, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-mint-green flex-shrink-0 mt-0.5" />
                  <span className="font-body text-text-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Teacher Dashboard Preview */}
      <section className="py-16 px-4 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-display text-sm text-mint-green font-bold">TEACHER DASHBOARD</span>
            <h2 className="font-display text-4xl text-text-dark mt-2">See Everything. Control Everything.</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: BarChart3, title: 'Real-time Accuracy', desc: 'See per-pupil accuracy as they type' },
              { icon: Target, title: 'Stage Progression', desc: 'Track advancement through 5 stages' },
              { icon: Clock, title: 'Session History', desc: 'Full history of every typing session' },
              { icon: Download, title: 'SENCO Export', desc: 'Export reports for intervention meetings' },
            ].map((feature, i) => (
              <div key={i} className="bg-white border-2 border-soft-gray/50 rounded-2xl p-6 shadow-md" data-testid={`dashboard-feature-${i}`}>
                <feature.icon className="w-8 h-8 text-mint-green mb-4" />
                <h3 className="font-display text-lg text-text-dark mb-2">{feature.title}</h3>
                <p className="font-body text-sm text-text-medium">{feature.desc}</p>
              </div>
            ))}
          </div>
          
          {/* Dashboard Mockup */}
          <div className="mt-12 bg-white border-2 border-mint-green/30 rounded-2xl overflow-hidden shadow-lg" data-testid="dashboard-mockup">
            <div className="bg-mint-green/10 px-4 py-2 flex items-center gap-2 border-b border-mint-green/20">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-coral-orange" />
                <div className="w-3 h-3 rounded-full bg-sunny-yellow" />
                <div className="w-3 h-3 rounded-full bg-mint-green" />
              </div>
              <span className="font-display text-xs text-text-medium ml-2">TYPETOWN TEACHER DASHBOARD · CLASS 4B</span>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Pupil Progress */}
                <div>
                  <h4 className="font-display text-xs text-text-medium mb-4">PUPIL PROGRESS THIS WEEK</h4>
                  {[
                    { name: 'Amara K.', stage: 3, accuracy: 94 },
                    { name: 'Ben T.', stage: 3, accuracy: 88 },
                    { name: 'Chloe M.', stage: 2, accuracy: 92 },
                    { name: 'Dylan R.', stage: 4, accuracy: 97 },
                    { name: 'Ella S.', stage: 1, accuracy: 78, alert: true },
                  ].map((pupil, i) => (
                    <div key={i} className="flex items-center gap-4 py-2 border-b border-soft-gray/50">
                      <div className="w-8 h-8 rounded-full bg-mint-green/20 flex items-center justify-center text-sm text-mint-green font-bold">
                        {pupil.name.charAt(0)}
                      </div>
                      <span className="font-body text-sm text-text-dark flex-1">{pupil.name}</span>
                      <span className="font-display text-xs text-text-medium">Stage {pupil.stage}</span>
                      <div className="w-24 h-2 bg-soft-gray rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${pupil.alert ? 'bg-sunny-yellow' : 'bg-mint-green'}`}
                          style={{ width: `${pupil.accuracy}%` }}
                        />
                      </div>
                      <span className={`font-display text-sm ${pupil.alert ? 'text-sunny-yellow' : 'text-mint-green'}`}>
                        {pupil.accuracy}%
                      </span>
                    </div>
                  ))}
                  <div className="mt-4 p-3 bg-sunny-yellow/10 border border-sunny-yellow/30 rounded-xl">
                    <span className="font-body text-sm text-sunny-yellow">
                      ⚠️ Ella S. below threshold - suggest a targeted session
                    </span>
                  </div>
                </div>
                
                {/* Class Map */}
                <div>
                  <h4 className="font-display text-xs text-text-medium mb-4">LIVE CLASS MAP · TYPETOWN ISLAND</h4>
                  <div className="aspect-square bg-mint-green/10 rounded-xl p-4">
                    <div className="grid grid-cols-8 grid-rows-8 gap-1 h-full">
                      {Array.from({ length: 64 }).map((_, i) => {
                        const isWater = i < 16 || i > 55;
                        const isForest = i >= 24 && i <= 40 && i % 8 > 3;
                        const isIsland = i >= 16 && i <= 55 && i % 8 <= 3;
                        return (
                          <div 
                            key={i}
                            className={`rounded-sm ${
                              isForest ? 'bg-mint-green/60' :
                              isIsland ? 'bg-bot-cyan/60' :
                              'bg-blue-900/30'
                            }`}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex gap-4 mt-4 justify-center">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm bg-bot-cyan/60" />
                      <span className="font-mono text-xs text-screen-white/50">Town</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm bg-mint-green/60" />
                      <span className="font-mono text-xs text-screen-white/50">Forest</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-4xl text-screen-white mb-4">Ready to Transform Typing?</h2>
          <p className="font-body text-screen-white/60 mb-8">Completely free for schools. No card required. Full features from day one.</p>
          <Link
            to="/trial"
            data-testid="schools-final-cta"
            className="font-display uppercase tracking-wider bg-zap-yellow text-deep-space px-10 py-4 text-xl rounded-sm hover:translate-y-1 hover:shadow-none shadow-[0_4px_0_0_#B2A000] transition-all inline-block"
          >
            Get Started Free
          </Link>
          <p className="font-mono text-sm text-screen-white/40 mt-4">Free for every school, every pupil - forever.</p>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ForSchoolsPage;
